import { PreviewManager } from '@/PreviewManager'
import { Easing, Tween } from '@tweenjs/tween.js'
import clsx from 'clsx'
import { tweenGroup } from './tweenGroup'
import {
	AmbientLight,
	ColorManagement,
	DataTexture,
	DirectionalLight,
	LinearSRGBColorSpace,
	RepeatWrapping,
	Scene,
	Texture,
	TextureLoader,
	WebGLRenderer
} from 'three'
import { EXRLoader } from 'three/examples/jsm/loaders/EXRLoader.js'
import { BODY_COLORS, BodyColor, Color, COLORS, SELECTION_COLORS } from '@/const'
import { Header } from '@/components/Header'
import { Accordion } from '@/components/Accordion'
import { Form } from '@/components/Form'
import { EventEmitter } from './EventEmitter'
import { Router } from './Router'
import { PointerControls } from './PointerControls'
import { Fade } from './Fade'
import { Logo } from '@/components/Logo'
import { Title } from '@/components/Title'
import { Stickers } from './Stickers'
import { Camera } from './Camera'
import { Sizes } from './Sizes'
import { Section } from '@/components/Section'

import normal from '../assets/normal.png'
import displacement from '../assets/displacement.exr'

import '../components/Footer.module.css'
import mainStyles from '../Main.module.css'
import headerStyles from '../components/Header.module.css'
import stickerStyles from '../components/Stickers.module.css'
import titleStyles from '../components/Title.module.css'
import logoStyles from '../components/Logo.module.css'
import sectionStyles from '../components/Section.module.css'
import pageStyles from '../components/Page.module.css'
import accordionStyles from '../components/Accordion.module.css'
import formStyles from '../components/Form.module.css'

const USE_PREVIEW = process.env.APP_ENV === 'staging'

class App extends EventEmitter {
	public readonly scene: Scene = new Scene()
	public readonly sizes: Sizes
	public readonly pointerControls: PointerControls
	public readonly intersectionObserver: IntersectionObserver
	public previewManager?: PreviewManager
	public pageYOffset = 0

	private readonly parser: DOMParser = new DOMParser()
	private readonly favicon: HTMLLinkElement
	private readonly router: Router
	private readonly renderer: WebGLRenderer
	private readonly container: HTMLElement
	private readonly camera: Camera
	private readonly fade: Fade

	private header!: Header
	private accordions: Accordion[] = []
	private forms: Form[] = []
	private sections: Section[] = []
	private logos: Logo[] = []
	private stickers: Stickers[] = []
	private titles: Title[] = []
	private bodyColor: BodyColor
	private normalTexture!: Texture
	private displacementTexture!: DataTexture
	private isLoaded = false
	private isFixed = false
	private needsRenderUpdate = false
	private needsScrollUpdate = false
	private tween?: Tween<{ top: number }>

	constructor() {
		super()
		this.requestRender = this.requestRender.bind(this)
		this.update = this.update.bind(this)
		window.scrollTo(0, 0)

		const bgColors = Object.keys(COLORS) as Color[]
		const bgColor = bgColors[Math.floor(Math.random() * bgColors.length)]
		const fgColor = SELECTION_COLORS[bgColor]
		document.documentElement.style.setProperty('--selection-fg', BODY_COLORS[fgColor as BodyColor])
		document.documentElement.style.setProperty('--selection-bg', COLORS[bgColor])

		this.bodyColor = document.body.classList.contains(mainStyles.Black) ? 'black' : 'white'
		this.container = document.querySelector(`.${mainStyles.Container}`) as HTMLElement
		this.intersectionObserver = new IntersectionObserver((entries) => this.emit('intersect', entries))

		this.sizes = new Sizes(this.container)
		this.pageYOffset = window.pageYOffset

		this.renderer = new WebGLRenderer({ antialias: window.devicePixelRatio < 2, alpha: true })
		this.renderer.setSize(this.sizes.width, this.sizes.height)
		this.renderer.setPixelRatio(this.sizes.pixelRatio)
		// keep pre-r152 behaviour: no color conversion, colors and textures used as-is
		ColorManagement.enabled = false
		this.renderer.outputColorSpace = LinearSRGBColorSpace
		this.container.appendChild(this.renderer.domElement)

		this.pointerControls = new PointerControls(this)
		this.camera = new Camera(this)
		this.fade = new Fade(this, this.bodyColor === 'white' ? 'black' : 'white')

		// intensities * PI to match legacy light units removed in r165
		const aLight = new AmbientLight(0xffffff, 0.75 * Math.PI)
		this.scene.add(aLight)

		const light = new DirectionalLight(0xffffff, 0.75 * Math.PI)
		light.position.set(2, 4, 1)
		this.scene.add(light)

		this.router = new Router()
		this.router.on('change', this.onNavigate.bind(this))

		this.favicon = document.querySelector("link[rel~='icon']") as HTMLLinkElement

		const mainNode = document.querySelector(`.${pageStyles.Main}`) as HTMLElement
		this.bodyColor = mainNode.dataset.bg as BodyColor
		this.isFixed = mainNode.dataset.fixed === 'true'

		window.addEventListener('scroll', this.scroll.bind(this))
		this.sizes.addEventListener('resize', this.resize.bind(this))
		this.resize()

		document.fonts.ready.then(() => this.init())
	}

	async init() {
		if (USE_PREVIEW) {
			const { PreviewManager } = await import('@/PreviewManager')
			this.previewManager = new PreviewManager()

			if (this.previewManager?.previewMode) {
				await this.getPage(window.location.pathname + window.location.search)
			}
		}

		requestAnimationFrame(this.update)

		await this.initPage()
		document.addEventListener('click', this.onDocumentClick.bind(this))

		document.body.style.opacity = '1'
		await this.fade.hide()
		this.showPage()
	}

	onDocumentClick(event: MouseEvent) {
		const target = event.target as HTMLAnchorElement
		if (target.tagName === 'A') {
			const { origin, hash } = new URL(target.href)
			if (origin === location.origin && !hash) {
				event.preventDefault()
				this.router.push(target.getAttribute('href') as string)
			}
		}
	}

	async onNavigate(route: string): Promise<void> {
		this.header.hide()
		await this.fade.show()
		window.scrollTo(0, 0)
		this.disposePage()
		await this.getPage(route)
		await this.initPage()
		await this.fade.hide()
		this.showPage()
	}

	async getPage(route: string): Promise<void> {
		let html = ''

		if (USE_PREVIEW && this.previewManager?.previewMode) {
			try {
				html = await this.previewManager.fetch(route)
			} catch (error) {
				console.error(error)
			}
		} else {
			const response = await fetch(`${route}index.html`)
			html = await response.text()
		}

		const doc = this.parser.parseFromString(html, 'text/html')
		const pageNode = doc.querySelector(`.${pageStyles.Main}`) as HTMLElement
		const wrapperNode = doc.querySelector(`.${mainStyles.Wrapper}`) as HTMLElement

		this.bodyColor = pageNode.dataset.bg as BodyColor
		this.isFixed = pageNode.dataset.fixed === 'true'

		document.body.className = clsx(
			mainStyles.Body,
			this.bodyColor === 'black' && mainStyles.Black,
			this.isFixed && mainStyles.IsFixed
		)

		document.title = doc.title
		document.documentElement.lang = doc.documentElement.lang
		this.favicon.href = `/favicon_${Math.floor(Math.random() * 8)}.png`

		document.querySelector(`.${mainStyles.Wrapper}`)?.replaceWith(wrapperNode)
	}

	async loadTextures() {
		const [normalTexture, displacementTexture] = await Promise.all([
			new TextureLoader().loadAsync(normal),
			new EXRLoader().loadAsync(displacement)
		])

		displacementTexture.wrapS = RepeatWrapping

		this.normalTexture = normalTexture
		this.displacementTexture = displacementTexture
		this.isLoaded = true
	}

	disposePage() {
		this.tween?.stop()

		this.sections.forEach((section) => section.dispose())
		this.sections.length = 0

		this.accordions.forEach((accordion) => accordion.dispose())
		this.accordions.length = 0

		this.forms.forEach((form) => form.dispose())
		this.forms.length = 0

		this.stickers.forEach((sticker) => sticker.dispose())
		this.stickers.length = 0

		this.titles.forEach((title) => title.dispose())
		this.titles.length = 0

		this.logos.forEach((logo) => logo.dispose())
		this.logos.length = 0

		this.header?.dispose()
	}

	async initPage() {
		if (!this.isLoaded) {
			await this.loadTextures()
		}

		const header = document.querySelector(`.${headerStyles.Main}`) as HTMLElement
		const sections = Array.from(document.querySelectorAll(`.${sectionStyles.Main}`) as NodeListOf<HTMLElement>)
		const accordions = Array.from(document.querySelectorAll(`.${accordionStyles.Main}`) as NodeListOf<HTMLElement>)
		const forms = Array.from(document.querySelectorAll(`.${formStyles.Main}`) as NodeListOf<HTMLElement>)
		const logos = Array.from(document.querySelectorAll(`.${logoStyles.Main}`) as NodeListOf<HTMLElement>)
		const titles = Array.from(document.querySelectorAll(`.${titleStyles.Main}`) as NodeListOf<HTMLElement>)
		const stickers = Array.from(document.querySelectorAll(`.${stickerStyles.Main}`) as NodeListOf<HTMLElement>)

		if (header) {
			this.header = new Header(header)
		}

		this.sections = sections.map((node) => new Section({ node, app: this }))
		this.accordions = accordions.map((node) => new Accordion(node))
		this.forms = forms.map((node) => new Form(node))
		this.logos = logos.map((node) => new Logo({ node, app: this }))
		this.titles = titles.map(
			(node) =>
				new Title({
					node,
					app: this,
					color: this.bodyColor === 'black' ? 'white' : 'black'
				})
		)
		this.stickers = stickers.map((node) => {
			return new Stickers({
				app: this,
				displacementTexture: this.displacementTexture,
				normalTexture: this.normalTexture,
				node
			})
		})

		await Promise.all(this.stickers.map((sticker) => sticker.load()))
		this.renderer.compile(this.scene, this.camera.getCamera())

		this.resize()
	}

	async showPage() {
		this.sections.forEach((section) => section.show())
		this.logos.forEach((logo) => logo.show())
		this.titles.forEach((title) => title.show())
		this.stickers.forEach((sticker) => sticker.update())
		this.header.show(this.router.activeRoute)
		this.pageYOffset = window.pageYOffset
		this.needsScrollUpdate = true
		this.needsRenderUpdate = true

		if (this.logos.length > 0) {
			this.tween = new Tween({ top: 0 }, tweenGroup)
				.to({ top: this.sizes.height * 2 }, 2000)
				.delay(500)
				.easing(Easing.Sinusoidal.InOut)
				.onUpdate(({ top }) => window.scrollTo(0, top))
				.start()
		}
	}

	requestRender() {
		this.needsRenderUpdate = true
	}

	scroll() {
		this.pageYOffset = window.pageYOffset
		this.needsScrollUpdate = true
		this.needsRenderUpdate = true
	}

	resize() {
		const { width, height } = this.sizes
		this.renderer.setSize(width, height)
		this.camera.resize()
		this.titles.forEach((title) => title.resize())
		this.logos.forEach((logo) => logo.resize())
		this.fade.resize()

		if (this.isFixed || this.sizes.isDesktop) {
			this.container.style.position = `fixed`
			this.container.style.transform = `none`
		} else {
			this.container.style.position = `absolute`
			this.container.style.transform = `translateY(${this.pageYOffset}px)`
		}

		this.pageYOffset = window.pageYOffset
		this.needsScrollUpdate = true
		this.needsRenderUpdate = true
	}

	update(time: number): void {
		requestAnimationFrame(this.update)
		tweenGroup.update(time)

		this.stickers.forEach((sticker) => sticker.updateVideo())

		if (this.needsScrollUpdate) {
			this.needsScrollUpdate = false

			if (!this.isFixed && !this.sizes.isDesktop) {
				this.container.style.transform = `translateY(${this.pageYOffset}px)`
			}

			if (!this.isFixed) {
				this.titles.forEach((title) => title.update())
				this.logos.forEach((logo) => logo.update())
			}

			this.stickers.forEach((sticker) => sticker.update())
		}

		if (this.needsRenderUpdate) {
			this.needsRenderUpdate = false
			this.camera.update()
			this.renderer.render(this.scene, this.camera.getCamera())
		}
	}
}

export default App
